<template>

  <div class="vflex">

    <div class="alert alert-secondary small">
      <i class="fa-solid fa-flask mr-1"></i>
      {{ $t('WhatsApp is in beta and, during this period, conversational agent tokens will not be charged. Before charging begins, we will let you know the prices in advance and each customer will be able to choose whether to keep using the feature.') }}
    </div>

    <!-- the header belongs to the conversation as a whole: it sits above both
    the thread and the contact panel -->
    <ConversationHeader :conversation="conversation">
      <template slot="actions">
        <ConversationAssigneeSelect
          class="mr-2"
          :users="users"
          :value="assignedUserId"
          :saving="savingAssignee"
          @input="changeAssignee" />

        <!-- conversational agent on/off for this conversation -->
        <button
          type="button"
          class="btn mr-2"
          :class="{ 'btn-info': isAgentEnabled, 'btn-outline-secondary': !isAgentEnabled }"
          :disabled="savingAgent || !accountAgentEnabled"
          :title="agentToggleLabel"
          @click="toggleAgent">
          <i
            v-if="savingAgent"
            class="fa-solid fa-circle-notch fa-spin"></i>
          <i
            v-else
            class="fa-solid fa-robot"></i>
        </button>

        <!-- read/unread toggle -->
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="savingRead"
          :title="readToggleLabel"
          @click="toggleRead">
          <i
            v-if="savingRead"
            class="fa-solid fa-circle-notch fa-spin"></i>
          <i
            v-else
            :class="isRead ? 'fa-solid fa-envelope-open' : 'fa-solid fa-envelope'"></i>
        </button>

        <ConversationOptions
          class="ml-2"
          :saving="deleting || unlinking"
          :can-unlink="hasCandidate"
          @unlink="unlink"
          @remove="remove" />
      </template>
    </ConversationHeader>

    <div class="vflex-row-1 hflex">
      <MessageThread
        class="hflex-col-1"
        :conversation="conversation"
        :channels="channels"
        :is-agent-enabled="isAgentEnabled"
        :saving-agent="savingAgent"
        @take-control="takeControl"
        @seen="markRead" />

      <div
        v-if="showContact"
        class="conversation-contact border-left">
        <ContactPanel
          :conversation="conversation"
          :loading="loading"
          @updated="$emit('updated')" />
      </div>
    </div>

  </div>

</template>

<script>
import ConversationHeader from './ConversationHeader'
import ConversationAssigneeSelect from './ConversationAssigneeSelect'
import ConversationOptions from './ConversationOptions'
import MessageThread from '../thread/MessageThread'
import ContactPanel from '../contact/ContactPanel'

export default {
  name: 'Conversation',
  components: {
    ConversationHeader,
    ConversationAssigneeSelect,
    ConversationOptions,
    MessageThread,
    ContactPanel
  },
  props: {
    conversation: {
      type: Object,
      default: null
    },
    channels: {
      type: Array,
      default: () => []
    },
    users: {
      type: Array,
      default: () => []
    },
    /*
    agentEnabled
    the account wide switches of the conversational agent, one per audience:
    { candidateAgentEnabled }. an audience turned off there keeps the agent
    away from the conversation no matter its own switch
    */
    agentEnabled: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    // the contact column is dropped when the conversation is already shown
    // inside the contact context (the candidate profile, for instance)
    showContact: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      savingAssignee: false,
      savingAgent: false,
      savingRead: false,
      deleting: false,
      unlinking: false,
      // set when the user marks the open conversation as unread, so the
      // automatic mark-as-read does not undo it while it stays open
      keepUnread: false
    }
  },
  computed: {
    conversationId () {
      return this.conversation && this.conversation._id
    },
    /*
    assignedUserId
    the api sends assigned_user_id populated ({ _id, name }); the select works
    with the id alone (it is what it emits back)
    */
    assignedUserId () {
      const assigned = this.conversation ? this.conversation.assigned_user_id : null

      if (!assigned) {
        return null
      }
      return typeof assigned === 'object' ? assigned._id : assigned
    },
    // the api sends candidate_id populated ({ _id, name, ... }) when the
    // conversation is associated to a candidate
    hasCandidate () {
      const candidate = this.conversation && this.conversation.candidate_id
      return !!(candidate && candidate._id)
    },
    /*
    accountAgentEnabled
    the account switch that covers this conversation. the agent only answers
    candidates today, so the candidate audience is the one that gates it
    */
    accountAgentEnabled () {
      return !!this.agentEnabled.candidateAgentEnabled
    },
    // the agent answers unless it was explicitly turned off in this
    // conversation, so an absent flag counts as enabled. the account switch
    // comes first: off there, off here
    isAgentEnabled () {
      return this.accountAgentEnabled && (!this.conversation || this.conversation.is_agent_enabled !== false)
    },
    agentToggleLabel () {
      if (!this.accountAgentEnabled) {
        return this.$t('The conversational agent is off for this account')
      }
      return this.isAgentEnabled ? this.$t('Disable agent') : this.$t('Enable agent')
    },
    isRead () {
      return !this.conversation || this.conversation.is_read !== false
    },
    readToggleLabel () {
      return this.isRead ? this.$t('Mark as unread') : this.$t('Mark as read')
    }
  },
  watch: {
    conversationId () {
      this.keepUnread = false
    }
  },
  methods: {
    /*
    markRead
    flags the conversation as read (when the thread is opened and when its list
    is scrolled to the bottom); skipped while the user is deliberately keeping
    it unread
    */
    markRead () {
      if (this.keepUnread) {
        return
      }
      if (this.conversation && this.conversation.is_read === false) {
        this.$store.dispatch('setConversationRead', { id: this.conversationId, isRead: true }).catch(() => {})
      }
    },
    /*
    toggleRead
    */
    toggleRead () {
      const isRead = !this.isRead
      this.savingRead = true

      this.$store
      .dispatch('setConversationRead', { id: this.conversationId, isRead: isRead })
      .then(() => {
        // keep it unread until the conversation is left and opened again
        this.keepUnread = !isRead
        this.$emit('updated')
      })
      .catch(() => {
        this.$store.dispatch('setWarn', { status: 'error' })
      })
      .finally(() => {
        this.savingRead = false
      })
    },
    /*
    changeAssignee
    */
    changeAssignee (userId) {
      this.savingAssignee = true

      this.$store
      .dispatch('updateConversation', { id: this.conversationId, data: { assigned_user_id: userId } })
      .then(() => {
        this.$emit('updated')
      })
      .catch(() => {
        this.$store.dispatch('setWarn', { status: 'error' })
      })
      .finally(() => {
        this.savingAssignee = false
      })
    },
    /*
    toggleAgent
    turns the conversational agent on/off for this conversation alone
    */
    toggleAgent () {
      this.setAgentEnabled(!this.isAgentEnabled)
    },
    /*
    takeControl
    the composer is blocked while the agent answers: taking it over is turning
    the agent off, so the user writes the next messages
    */
    takeControl () {
      this.setAgentEnabled(false)
    },
    /*
    setAgentEnabled
    */
    setAgentEnabled (isAgentEnabled) {
      this.savingAgent = true

      this.$store
      .dispatch('updateConversation', { id: this.conversationId, data: { is_agent_enabled: isAgentEnabled } })
      .then(() => {
        this.$emit('updated')
      })
      .catch(() => {
        this.$store.dispatch('setWarn', { status: 'error' })
      })
      .finally(() => {
        this.savingAgent = false
      })
    },
    /*
    unlink
    detaches the conversation from its candidate. the name goes along because
    the update rewrites the editable fields
    */
    unlink () {
      this.unlinking = true

      const data = {
        name: (this.conversation && this.conversation.name) || null,
        candidate_id: null
      }

      this.$store
      .dispatch('updateConversation', { id: this.conversationId, data: data })
      .then(() => {
        this.$store.dispatch('setWarn', { status: 'success' })
        this.$emit('updated')
      })
      .catch(() => {
        this.$store.dispatch('setWarn', { status: 'error' })
      })
      .finally(() => {
        this.unlinking = false
      })
    },
    /*
    remove
    deletes the conversation (and with it the whole message history), after
    confirmation
    */
    remove () {
      const deleteAction = () => {
        this.deleting = true

        this.$store
        .dispatch('deleteConversation', this.conversationId)
        .then(() => {
          this.$store.dispatch('setWarn', { status: 'success' })
          this.$emit('deleted')
        })
        .catch(() => {
          this.$store.dispatch('setWarn', { status: 'error' })
        })
        .finally(() => {
          this.deleting = false
        })
      }

      this.$store.dispatch('setAlertModal', {
        title: this.$t('Remove this contact?'),
        message: this.$t('All the messages exchanged with this contact will be removed. This operation cannot be undone.'),
        action: deleteAction
      })
    }
  }
}
</script>

<style scoped>
/* fixed 300px column: flex-basis instead of min-width so the content cannot
   stretch it, and min-width 0 so the default auto minimum does not either */
.conversation-contact {
  flex: 0 0 300px;
  min-width: 0;
  overflow: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/Messaging/conversation/Conversation.vue